/**
 * useAISearchPageView Hook
 *
 * Custom hook for tracking AI Search PAGEVIEW events in the chat renderer.
 * Implements tracking requirements from Conversational AI Search Tracking Requirements.
 *
 * @see https://marriottcloud.atlassian.net/wiki/spaces/DNAT/pages/3209003032
 */

import { useEffect, useRef, useCallback } from 'react';
import { useSession } from '@marriott/mi-headless-utils';
import {
  pageViewACDLkeyGenerator,
  AI_SEARCH_PAGE_NAMES,
  AI_SEARCH_TEMPLATES,
  ACDL_EVENT_NAMES,
} from '../utils/acdlUtils';

type DataLayerEventType = {
  event: string;
  skipDatalayerUpdate?: boolean;
  [key: string]: { [key: string]: unknown } | string | undefined | boolean;
};

export type AISearchPageName = (typeof AI_SEARCH_PAGE_NAMES)[keyof typeof AI_SEARCH_PAGE_NAMES];
export type AISearchTemplate = (typeof AI_SEARCH_TEMPLATES)[keyof typeof AI_SEARCH_TEMPLATES];

export interface UseAISearchPageViewOptions {
  /** The page name to track (e.g., 'AI Search|Join or Login') */
  pageName: AISearchPageName;
  /** Whether this is a modal view */
  isModal?: boolean;
  /** The page template (for search results) */
  pageTemplate?: AISearchTemplate;
  /** Custom page URL (defaults to current URL) */
  pageUrl?: string;
  /** Whether to track immediately on mount */
  trackOnMount?: boolean;
  /** Dependencies that should trigger a re-track when changed */
  dependencies?: unknown[];
}

export interface UseAISearchPageViewReturn {
  /** Manually trigger a PAGEVIEW event */
  trackPageView: (overrides?: Partial<UseAISearchPageViewOptions>) => void;
  /** Whether ACDL is enabled */
  isACDLEnabled: boolean;
  /** Whether the datalayer is ready */
  isDataLayerReady: boolean;
}

/**
 * Hook for tracking AI Search PAGEVIEW events
 *
 * @example
 * // Track "AI Search|Join or Login" page view when component mounts
 * const { trackPageView, isACDLEnabled } = useAISearchPageView({
 *   pageName: AI_SEARCH_PAGE_NAMES.JOIN_OR_LOGIN,
 *   isModal: false,
 *   trackOnMount: true,
 * });
 *
 * @example
 * // Track search results with template
 * const { trackPageView } = useAISearchPageView({
 *   pageName: AI_SEARCH_PAGE_NAMES.SEARCH_RESULTS,
 *   pageTemplate: AI_SEARCH_TEMPLATES.INTEGRATED_IMAGERY,
 *   trackOnMount: false,
 * });
 *
 * // Call when results are ready
 * useEffect(() => {
 *   if (searchResults) {
 *     trackPageView();
 *   }
 * }, [searchResults]);
 */
export function useAISearchPageView(options: UseAISearchPageViewOptions): UseAISearchPageViewReturn {
  const { pageName, isModal = false, pageTemplate, pageUrl, trackOnMount = true, dependencies = [] } = options;

  const { isACDLenabled, datalayer, updateDatalayer } = useSession();
  const hasTrackedRef = useRef(false);
  const isDataLayerReady = Boolean(isACDLenabled && datalayer);

  /**
   * Track a PAGEVIEW event
   */
  const trackPageView = useCallback(
    (overrides?: Partial<UseAISearchPageViewOptions>) => {
      if (!isACDLenabled || !datalayer) {
        console.debug('[useAISearchPageView] ACDL not enabled or datalayer not ready');
        return;
      }

      const finalPageName = overrides?.pageName ?? pageName;
      const finalIsModal = overrides?.isModal ?? isModal;
      const finalPageTemplate = overrides?.pageTemplate ?? pageTemplate;
      const finalPageUrl = overrides?.pageUrl ?? pageUrl;

      const event = pageViewACDLkeyGenerator({
        datalayer,
        pageName: finalPageName,
        isModal: finalIsModal,
        pageTemplate: finalPageTemplate,
        pageUrl: finalPageUrl,
      });

      console.debug('[useAISearchPageView] Tracking PAGEVIEW:', event);

      updateDatalayer(event as DataLayerEventType);
    },
    [isACDLenabled, datalayer, updateDatalayer, pageName, isModal, pageTemplate, pageUrl]
  );

  /**
   * Track on mount if enabled
   */
  useEffect(() => {
    if (trackOnMount && isDataLayerReady && !hasTrackedRef.current) {
      hasTrackedRef.current = true;
      trackPageView();
    }
  }, [trackOnMount, isDataLayerReady, trackPageView]);

  /**
   * Re-track when dependencies change (if trackOnMount is true)
   */
  useEffect(() => {
    if (trackOnMount && isDataLayerReady && dependencies.length > 0) {
      trackPageView();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...dependencies]);

  return {
    trackPageView,
    isACDLEnabled: isACDLenabled,
    isDataLayerReady,
  };
}

/**
 * Convenience hook for tracking "AI Search|Join or Login" page view
 * Used when unauthenticated or remembered users see the sign-in prompt
 */
export function useAISearchJoinOrLoginPageView(trackOnMount = true): UseAISearchPageViewReturn {
  return useAISearchPageView({
    pageName: AI_SEARCH_PAGE_NAMES.JOIN_OR_LOGIN,
    isModal: false,
    trackOnMount,
  });
}

/**
 * Convenience hook for tracking "AI Search|Welcome" page view
 * Used when authenticated users see the welcome screen
 */
export function useAISearchWelcomePageView(trackOnMount = true): UseAISearchPageViewReturn {
  return useAISearchPageView({
    pageName: AI_SEARCH_PAGE_NAMES.WELCOME,
    isModal: false,
    trackOnMount,
  });
}

/**
 * Convenience hook for tracking "Global Login" modal page view
 * Used when the global login flyout is opened
 */
export function useGlobalLoginPageView(trackOnMount = true): UseAISearchPageViewReturn {
  return useAISearchPageView({
    pageName: AI_SEARCH_PAGE_NAMES.GLOBAL_LOGIN,
    isModal: true,
    trackOnMount,
  });
}

/**
 * Convenience hook for tracking "AI Search|Property Details" page view
 * Used when the Hotel Quick View (HQV) panel is opened
 */
export function useAISearchPropertyDetailsPageView(trackOnMount = false): UseAISearchPageViewReturn {
  return useAISearchPageView({
    pageName: AI_SEARCH_PAGE_NAMES.PROPERTY_DETAILS,
    isModal: false,
    trackOnMount,
  });
}

/**
 * Convenience hook for tracking "AI Search|Search Results" page view
 * @param uiType - The UXL uiType to determine the template
 * @deprecated Use useAISearchResultsTracking for imperative tracking
 */
export function useAISearchResultsPageView(uiType?: string, trackOnMount = false): UseAISearchPageViewReturn {
  /**
   * Get the page template based on UXL uiType
   * Maps uiType to the corresponding ACDL template value
   */
  const getTemplate = (): AISearchTemplate | undefined => {
    switch (uiType) {
      case 'integratedImagery':
        return AI_SEARCH_TEMPLATES.INTEGRATED_IMAGERY;
      case 'categoryList':
        return AI_SEARCH_TEMPLATES.CATEGORY_LIST;
      case 'default':
        return AI_SEARCH_TEMPLATES.DEFAULT;
      case 'redirectBlock':
        return AI_SEARCH_TEMPLATES.REDIRECT_BLOCK;
      case 'noResults':
        return AI_SEARCH_TEMPLATES.NO_RESULTS;
      case 'systemError':
        return AI_SEARCH_TEMPLATES.SYSTEM_ERROR;
      default:
        return undefined;
    }
  };

  /**
   * Get the page name based on UXL uiType
   * Template A/B use "AI Search|Search Results"
   * Edge cases use specific page names
   */
  const getPageName = (): AISearchPageName => {
    switch (uiType) {
      case 'integratedImagery':
      case 'categoryList':
        return AI_SEARCH_PAGE_NAMES.SEARCH_RESULTS;
      case 'default':
        return AI_SEARCH_PAGE_NAMES.SEARCH_DEFAULT;
      case 'redirectBlock':
        return AI_SEARCH_PAGE_NAMES.SEARCH_REDIRECT_BLOCK;
      case 'noResults':
        return AI_SEARCH_PAGE_NAMES.SEARCH_NO_RESULTS;
      case 'systemError':
        return AI_SEARCH_PAGE_NAMES.SYSTEM_ERROR;
      default:
        return AI_SEARCH_PAGE_NAMES.SEARCH_RESULTS;
    }
  };

  return useAISearchPageView({
    pageName: getPageName(),
    isModal: false,
    pageTemplate: getTemplate(),
    trackOnMount,
    dependencies: [uiType],
  });
}

/**
 * Helper functions to get page name and template from UXL uiType
 */
export function getSearchResultsPageName(uiType?: string): AISearchPageName {
  switch (uiType) {
    case 'integratedImagery':
    case 'categoryList':
      return AI_SEARCH_PAGE_NAMES.SEARCH_RESULTS;
    case 'default':
      return AI_SEARCH_PAGE_NAMES.SEARCH_DEFAULT;
    case 'redirectBlock':
      return AI_SEARCH_PAGE_NAMES.SEARCH_REDIRECT_BLOCK;
    case 'noResults':
      return AI_SEARCH_PAGE_NAMES.SEARCH_NO_RESULTS;
    case 'systemError':
      return AI_SEARCH_PAGE_NAMES.SYSTEM_ERROR;
    default:
      return AI_SEARCH_PAGE_NAMES.SEARCH_RESULTS;
  }
}

export function getSearchResultsTemplate(uiType?: string): AISearchTemplate | undefined {
  switch (uiType) {
    case 'integratedImagery':
      return AI_SEARCH_TEMPLATES.INTEGRATED_IMAGERY;
    case 'categoryList':
      return AI_SEARCH_TEMPLATES.CATEGORY_LIST;
    case 'default':
      return AI_SEARCH_TEMPLATES.DEFAULT;
    case 'redirectBlock':
      return AI_SEARCH_TEMPLATES.REDIRECT_BLOCK;
    case 'noResults':
      return AI_SEARCH_TEMPLATES.NO_RESULTS;
    case 'systemError':
      return AI_SEARCH_TEMPLATES.SYSTEM_ERROR;
    default:
      return undefined;
  }
}

export interface UseAISearchResultsTrackingReturn {
  /** Track search results PAGEVIEW with dynamic uiType */
  trackSearchResultsPageView: (uiType?: string) => void;
  /** Whether ACDL is enabled */
  isACDLEnabled: boolean;
  /** Whether the datalayer is ready */
  isDataLayerReady: boolean;
}

/**
 * Hook for tracking AI Search Results PAGEVIEW events imperatively
 * Use this when you need to track search results with a dynamic uiType
 *
 * @example
 * const { trackSearchResultsPageView } = useAISearchResultsTracking();
 *
 * // When search results are received
 * trackSearchResultsPageView('integratedImagery'); // Template A
 * trackSearchResultsPageView('categoryList'); // Template B
 * trackSearchResultsPageView('default'); // Edge case 1
 * trackSearchResultsPageView('redirectBlock'); // Edge case 2
 * trackSearchResultsPageView('noResults'); // Edge case 3
 * trackSearchResultsPageView('systemError'); // Edge case 4
 */
export function useAISearchResultsTracking(): UseAISearchResultsTrackingReturn {
  const { isACDLenabled, datalayer, updateDatalayer } = useSession();
  const isDataLayerReady = Boolean(isACDLenabled && datalayer);

  const trackSearchResultsPageView = useCallback(
    (uiType?: string) => {
      if (!isACDLenabled || !datalayer) {
        console.debug('[useAISearchResultsTracking] ACDL not enabled or datalayer not ready');
        return;
      }

      const pageName = getSearchResultsPageName(uiType);
      const pageTemplate = getSearchResultsTemplate(uiType);

      const event = pageViewACDLkeyGenerator({
        datalayer,
        pageName,
        isModal: false,
        pageTemplate,
      });

      console.debug('[useAISearchResultsTracking] Tracking PAGEVIEW:', event);

      updateDatalayer(event as DataLayerEventType);
    },
    [isACDLenabled, datalayer, updateDatalayer]
  );

  return {
    trackSearchResultsPageView,
    isACDLEnabled: isACDLenabled,
    isDataLayerReady,
  };
}

export { AI_SEARCH_PAGE_NAMES, AI_SEARCH_TEMPLATES, ACDL_EVENT_NAMES };
